/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — auth-billing.ts
   Stripe subscription, trial banner, plan selection, terms acceptance.
   ═══════════════════════════════════════════════════════════════════════ */

import { state } from './state';
import {
  FIREBASE_ENABLED,
  STRIPE_CONFIG,
  STRIPE_ENABLED,
  getFirebaseUid,
  isAdmin,
} from './config';
import { el, showToast } from './utils';
import {
  getShowDashboardFn,
  hasAcceptedTerms,
  showTermsGate,
  persistTermsAcceptance,
  hideTermsGate,
  showSubscriptionGate,
  hideSubscriptionGate,
} from './auth-helpers';

declare const firebase: any;

let selectedPlan: string = 'individual';

// ─── Subscription Management ────────────────────────────────────────────────

export function checkSubscription(callback: (hasAccess: boolean) => void): void {
  if (!STRIPE_ENABLED || !FIREBASE_ENABLED) { callback(true); return; }

  if (isAdmin()) { callback(true); return; }

  const uid: string | null = state.firebaseAuth.currentUser ? state.firebaseAuth.currentUser.uid : null;
  if (!uid) { callback(false); return; }

  state.firebaseDb.collection('users').doc(uid).get()
    .then(function(doc: any) {
      if (!doc.exists) { callback(true); return; }
      const data = doc.data();
      const status: string = data.subscriptionStatus || 'trial';

      if (status === 'active' || status === 'trialing') {
        if (data.firm) {
          const btn = document.getElementById('team-mgmt-btn');
          if (btn) btn.style.display = 'inline-block';
        }
        callback(true);
        return;
      }

      if (status === 'trial') {
        const trialEnd: Date | null = data.trialEndsAt ? new Date(data.trialEndsAt) : null;
        if (trialEnd && trialEnd > new Date()) {
          const daysLeft: number = Math.ceil((trialEnd.getTime() - new Date().getTime()) / (24 * 60 * 60 * 1000));
          showTrialBanner(daysLeft);
          callback(true);
          return;
        }
      }

      checkFirmMembership(state.currentUserEmail!, function(isFirmMember: boolean) {
        callback(isFirmMember);
      });
    })
    .catch(function(err: any) {
      console.warn('Subscription check failed:', err);
      showToast('Sign in failed: ' + err.message, 'error');
      callback(false);
    });
}

function showTrialBanner(daysLeft: number): void {
  const banner = document.getElementById('sub-trial-banner');
  if (banner) {
    const elNode = document.getElementById('trial-days-left');
    if (elNode) elNode.textContent = String(daysLeft);
    banner.style.display = 'block';
  }
}

export function dismissTrialBanner(): void {
  const banner = document.getElementById('sub-trial-banner');
  if (banner) banner.style.display = 'none';
}

// Check if current user is a member of any firm
function checkFirmMembership(email: string, callback: (isMember: boolean) => void): void {
  if (!FIREBASE_ENABLED || !getFirebaseUid()) { callback(false); return; }

  state.firebaseDb.collection('users')
    .where('firm.memberEmails', 'array-contains', email)
    .limit(1)
    .get()
    .then(function(snapshot: any) {
      if (!snapshot.empty) {
        const firmDoc = snapshot.docs[0].data();
        const firmStatus: string = firmDoc.subscriptionStatus;
        callback(firmStatus === 'active' || firmStatus === 'trialing');
      } else {
        callback(false);
      }
    })
    .catch(function() { callback(false); });
}

// ─── Terms acceptance ───────────────────────────────────────────────────────

export function acceptTerms(): void {
  if (!state.currentUserEmail) return;

  persistTermsAcceptance();
  hideTermsGate();

  checkSubscription(function (hasAccess: boolean) {
    if (hasAccess) {
      hideSubscriptionGate();
      const fn = getShowDashboardFn();
      if (fn) fn();
    } else {
      showSubscriptionGate();
    }
  });
}

// ─── Post-login gate routing ────────────────────────────────────────────────

export function runPostLoginGate(): void {
  if (!hasAcceptedTerms()) {
    showTermsGate();
    return;
  }
  checkSubscription(function (hasAccess: boolean) {
    if (hasAccess) {
      hideSubscriptionGate();
      const fn = getShowDashboardFn();
      if (fn) fn();
    } else {
      showSubscriptionGate();
    }
  });
}

// ─── Plan selection / checkout ──────────────────────────────────────────────

export function selectPlan(plan: string): void {
  selectedPlan = plan;
  const indCard = el('plan-individual');
  const firmCard = el('plan-firm');
  const firmPlusCard = el('plan-firmplus');
  const btn = el('subscribe-btn');
  if (!indCard || !firmCard || !firmPlusCard || !btn) return;

  const cards = [indCard, firmCard, firmPlusCard];
  cards.forEach(card => {
    card.style.borderColor = 'var(--border)';
    card.style.boxShadow = 'none';
  });
  firmCard.style.borderColor = 'var(--border)';

  if (plan === 'firm') {
    firmCard.style.borderColor = 'var(--primary)';
    firmCard.style.boxShadow = '0 0 0 2px var(--primary)';
    btn.textContent = 'Subscribe — Firm $499/mo';
  } else if (plan === 'firmplus') {
    firmPlusCard.style.borderColor = 'var(--primary)';
    firmPlusCard.style.boxShadow = '0 0 0 2px var(--primary)';
    btn.textContent = 'Subscribe — Firm+ $750/mo';
  } else {
    indCard.style.borderColor = 'var(--primary)';
    indCard.style.boxShadow = '0 0 0 2px var(--primary)';
    btn.textContent = 'Subscribe — Individual $99/mo';
  }
}

export function startCheckout(): void {
  const link: string | undefined = selectedPlan === 'firm' ? STRIPE_CONFIG.firmPaymentLink : selectedPlan === 'firmplus' ? STRIPE_CONFIG.firmPlusPaymentLink : STRIPE_CONFIG.paymentLink;
  if (!link) {
    alert('Payment link not configured yet. Please contact support.');
    return;
  }
  const uid: string | null = getFirebaseUid();
  const email: string = state.currentUserEmail || '';
  let url: string = link + '?prefilled_email=' + encodeURIComponent(email);
  if (uid) url += '&client_reference_id=' + encodeURIComponent(uid);
  window.open(url, '_blank');
}
